/**
 * Layout de diagrama de classes: só geometria.
 *
 * Não conhece coleção, propriedade nem fluxo — recebe caixas com uma altura já
 * calculada e arestas por alias, e devolve coordenadas. É o que permite mudar a
 * notação sem tocar no posicionamento, e testar o posicionamento sem montar um
 * workspace.
 *
 * A altura vem de fora, e não de uma medição do DOM, porque a caixa tem largura fixa e
 * linhas de uma linha só: nesse regime a altura é aritmética. Medir exigiria renderizar
 * antes de posicionar, e o primeiro quadro sairia com tudo empilhado na origem.
 *
 * Tudo aqui é calculado num par de eixos abstratos — PRINCIPAL, a direção em que as
 * camadas avançam, e TRANSVERSAL, em que as caixas de uma mesma camada se empilham — e
 * só no fim vira x/y. É o que permite virar o diagrama de lado sem uma segunda
 * implementação: na horizontal o principal é o x, na vertical é o y.
 */

export const BOX_WIDTH = 260

/**
 * Métricas da caixa. São a ÚNICA fonte: o CSS as recebe como custom properties, então
 * a altura que o layout calcula e a que o navegador desenha não podem divergir. Mudar
 * um número aqui move o desenho e as linhas juntos.
 */
export const METRICS = {
    header: 34,
    row: 22,
    /** Faixa de título de um compartimento (o nome do fluxo) */
    section: 24,
    /** Folga acima e abaixo das linhas de um compartimento */
    padding: 6,
    /** Espessura da divisória entre compartimentos */
    divider: 1,
    /** Borda da caixa. Conta duas vezes: com box-sizing: border-box ela come altura útil */
    border: 1
}

/**
 * As métricas como custom properties, para o CSS da caixa desenhar os mesmos números que
 * o layout somou. Mora aqui, com elas, e não em cada componente de caixa: são duas, e a
 * segunda cópia seria a que sairia de sincronia.
 */
export function boxProperties(): Record<string, string> {
    return {
        '--class-box-width': `${BOX_WIDTH}px`,
        '--class-box-header': `${METRICS.header}px`,
        '--class-box-row': `${METRICS.row}px`,
        '--class-box-section': `${METRICS.section}px`,
        '--class-box-padding': `${METRICS.padding}px`
    }
}

/**
 * Vão entre camadas. Na horizontal precisa de mais espaço porque os dois rótulos de
 * multiplicidade ficam lado a lado no mesmo vão; na vertical eles ficam um acima do
 * outro e sobra folga.
 */
const MAIN_GAP = {horizontal: 110, vertical: 72}
const CROSS_GAP = 28
const PADDING = 24

/** Distância que a linha anda para fora da caixa antes de virar. */
const STUB = 22

/** Espaço mínimo entre duas ligações na mesma borda, quando pedem o mesmo ponto. */
const MIN_ANCHOR_GAP = 9

/** Passagem que uma ligação longa reserva na camada que atravessa. */
const CROSSING_SIZE = 16

/** Quanto uma alça se afasta da caixa, e quanto a seguinte se afasta mais que ela. */
const SELF_OUT = STUB + 18
const SELF_STEP = 16

export type Orientation = 'horizontal' | 'vertical'

export type DiagramNode = { alias: string; height: number }

/**
 * O `id` volta na aresta posicionada para quem chamou reencontrar o que ela significa.
 * Sem ele só restaria casar por posição na lista, e o layout DESCARTA aresta órfã — o
 * que desalinharia geometria e rótulo em silêncio, justamente no modelo com defeito.
 */
export type DiagramLink = {
    from: string
    to: string
    id: number
    /**
     * Onde encostar na caixa, medido do topo dela — a linha da propriedade que a ligação
     * representa. Opcional: sem isso as arestas se distribuem igualmente pela borda.
     *
     * Vale só com as camadas lado a lado. Empilhadas, a linha sai pela base e entra pelo
     * topo, então o ponto varia na HORIZONTAL enquanto as linhas da caixa se empilham na
     * vertical: não há a que alinhar.
     */
    fromAnchor?: number
    toAnchor?: number
    /**
     * Qual das pontas precisa vir ANTES na ordem das camadas.
     *
     * Por padrão a origem, porque a ligação se lê dela para o destino. Mas quando é a
     * origem que depende do destino para existir, é o destino que precisa já estar
     * desenhado quando ela aparecer — e aí vale `to`.
     */
    first?: 'from' | 'to'
}

export type LaidOutBox = { alias: string; x: number; y: number; width: number; height: number }

export type LaidOutEdge = {
    id: number
    from: string
    to: string
    /** Polilinha em coordenadas do diagrama */
    points: { x: number; y: number }[]
    /** Onde ancorar cada rótulo de multiplicidade */
    source: { x: number; y: number }
    target: { x: number; y: number }
    /** No sentido em que as camadas avançam? Decide de que lado do ponto o rótulo fica */
    forward: boolean
    selfLink: boolean
}

export type Diagram = {
    boxes: LaidOutBox[]
    edges: LaidOutEdge[]
    width: number
    height: number
    orientation: Orientation
}

/**
 * Nó do layout. Além das caixas de verdade, entram os nós de PASSAGEM: sem-corpo,
 * criados numa camada que uma ligação longa atravessa, só para reservar a faixa por onde
 * ela passa. Sem eles a ligação cruzava a camada em linha reta e saía por trás de uma
 * caixa.
 */
type LayoutNode = DiagramNode & { crossing: boolean }

type PlacedNode = LaidOutBox & { crossing: boolean }

/**
 * As pontas de uma ligação na ordem em que as camadas as desenham, com a âncora do lado
 * que vem antes. Um lugar só, porque a ordem das camadas e a ordenação dentro delas
 * precisam concordar: se divergirem, o baricentro ordena pela caixa errada.
 */
function endsOf(link: DiagramLink) {
    const reversed = link.first === 'to'
    return {
        earlier: reversed ? link.to : link.from,
        later: reversed ? link.from : link.to,
        anchor: reversed ? link.toAnchor : link.fromAnchor
    }
}

/**
 * Converte o par (principal, transversal) em x/y. É o único lugar que conhece a
 * diferença entre as duas orientações.
 */
function project(orientation: Orientation, main: number, cross: number) {
    return orientation === 'horizontal' ? {x: main, y: cross} : {x: cross, y: main}
}

/** Quanto o nó ocupa na direção das camadas. O de passagem não engorda a coluna. */
function mainExtent(orientation: Orientation, node: LayoutNode): number {
    if (node.crossing) return 0
    return orientation === 'horizontal' ? BOX_WIDTH : node.height
}

/** Quanto o nó ocupa na direção em que os nós de uma camada se empilham. */
function crossExtent(orientation: Orientation, node: LayoutNode): number {
    if (node.crossing) return CROSSING_SIZE
    return orientation === 'horizontal' ? node.height : BOX_WIDTH
}

/** Onde o nó começa e termina na direção das camadas. */
function mainRange(orientation: Orientation, box: LaidOutBox) {
    return orientation === 'horizontal'
        ? {start: box.x, end: box.x + box.width}
        : {start: box.y, end: box.y + box.height}
}

/** Onde o nó começa, e quanto mede, na direção transversal. */
function crossRange(orientation: Orientation, box: LaidOutBox) {
    return orientation === 'horizontal'
        ? {start: box.y, size: box.height}
        : {start: box.x, size: box.width}
}

function centreOf(orientation: Orientation, box: LaidOutBox) {
    const {start, size} = crossRange(orientation, box)
    return start + size / 2
}

/**
 * Camada de cada caixa: o destino fica depois da origem.
 *
 * Relaxação com teto de |V| passadas em vez de ordenação topológica porque o modelo
 * PODE ter ciclo (A aponta para B que aponta para A é legítimo aqui). Sem o teto, um
 * ciclo faria isto rodar para sempre; com ele, o ciclo simplesmente para de empurrar.
 *
 * Isto sozinho dá a camada MAIS CEDO possível, e é onde ficava o problema: toda coleção
 * independente caía na primeira coluna, mesmo a que só é usada lá no fim. O `pullLate`
 * logo abaixo desfaz isso.
 */
function layerOf(nodes: DiagramNode[], links: DiagramLink[]): Record<string, number> {
    const layer: Record<string, number> = {}
    nodes.forEach(node => { layer[node.alias] = 0 })
    for (let pass = 0; pass < nodes.length; pass++) {
        let moved = false
        links.forEach(link => {
            if (link.from === link.to) return
            const {earlier, later} = endsOf(link)
            const wanted = layer[earlier] + 1
            if (layer[later] < wanted) {
                layer[later] = wanted
                moved = true
            }
        })
        if (!moved) break
    }
    pullLate(nodes, links, layer)
    return layer
}

/**
 * Aproxima cada caixa de quem depende dela: empurra-a para a última coluna em que ainda
 * cabe, logo antes da primeira que a usa.
 *
 * Sem isto, um ambiente com três coleções independentes e uma cadeia de identidade
 * curta empilhava as três na primeira coluna e deixava as outras com uma só — não
 * porque o modelo mandasse, mas porque a camada mais cedo possível é o padrão do
 * algoritmo. Uma coleção usada só lá na frente não tem razão para abrir o desenho.
 *
 * Quem não é usado por ninguém fica onde está. É o que segura as duas pontas: a coleção
 * mais dependente de todas continua definindo a última coluna, e a coleção SOLTA, sem
 * relacionamento nenhum, fica na primeira em vez de ir parar no fim junto das mais
 * derivadas, onde não significaria nada.
 */
function pullLate(nodes: DiagramNode[], links: DiagramLink[], layer: Record<string, number>) {
    const after: Record<string, string[]> = {}
    links.forEach(link => {
        if (link.from === link.to) return
        const {earlier, later} = endsOf(link)
        if (!after[earlier]) after[earlier] = []
        after[earlier].push(later)
    })

    for (let pass = 0; pass < nodes.length; pass++) {
        let moved = false
        nodes.forEach(node => {
            const users = after[node.alias]
            if (!users || !users.length) return
            const latest = Math.min(...users.map(alias => layer[alias])) - 1
            if (latest > layer[node.alias]) {
                layer[node.alias] = latest
                moved = true
            }
        })
        if (!moved) break
    }
}

/**
 * Quebra as ligações longas em trechos de uma camada só, criando um nó de passagem em
 * cada camada intermediária.
 *
 * É o que impede a linha de sair por trás de uma caixa: o nó de passagem ocupa uma faixa
 * na coluna, as caixas de verdade se afastam dela, e a ligação atravessa por ali. Os
 * trechos também entram no baricentro, então a passagem se posiciona perto das pontas em
 * vez de cortar a coluna em qualquer lugar.
 */
function splitLongLinks(nodes: DiagramNode[], links: DiagramLink[], layer: Record<string, number>) {
    const all: LayoutNode[] = nodes.map(node => ({...node, crossing: false}))
    const steps: DiagramLink[] = []
    const paths: Record<number, string[]> = {}

    links.forEach(link => {
        if (link.from === link.to) {
            paths[link.id] = [link.from]
            return
        }
        const {earlier, later, anchor} = endsOf(link)
        const path = [earlier]
        for (let level = layer[earlier] + 1; level < layer[later]; level++) {
            //Prefixo impossível num alias de coleção, para não colidir com nó de verdade
            const alias = ` crossing:${link.id}:${level}`
            all.push({alias, height: CROSSING_SIZE, crossing: true})
            layer[alias] = level
            path.push(alias)
        }
        path.push(later)
        paths[link.id] = path

        for (let i = 0; i + 1 < path.length; i++) {
            //A âncora só vale no primeiro trecho: os seguintes saem de um nó de passagem
            steps.push({from: path[i], to: path[i + 1], id: link.id, fromAnchor: i ? undefined : anchor})
        }
    })

    return {all, steps, paths}
}

/** Nós agrupados por camada, na ordem das camadas. */
function layersOf(nodes: LayoutNode[], layer: Record<string, number>): LayoutNode[][] {
    const depth = Math.max(0, ...nodes.map(node => layer[node.alias]))
    const layers: LayoutNode[][] = []
    for (let i = 0; i <= depth; i++) layers.push([])
    nodes.forEach(node => layers[layer[node.alias]].push(node))
    return layers
}

/**
 * Ordena cada camada pela média da posição dos vizinhos na camada anterior (baricentro),
 * que é a heurística clássica para reduzir cruzamento de linhas. Duas varreduras bastam:
 * a terceira já quase não muda nada e o custo cresce.
 */
function sortLayers(layers: LayoutNode[][], steps: DiagramLink[], heights: Record<string, number>) {
    /** Por nó: de quem ele vem, e de que altura daquele nó. */
    type Incidence = { neighbour: string; anchor?: number }
    const arriving: Record<string, Incidence[]> = {}
    steps.forEach(step => {
        if (step.from === step.to) return
        if (!arriving[step.to]) arriving[step.to] = []
        arriving[step.to].push({neighbour: step.from, anchor: step.fromAnchor})
    })

    /**
     * A que altura do nó anterior a ligação sai, de 0 a 1.
     *
     * Entra no baricentro como fração somada à posição do nó, e é o que ordena duas
     * caixas ligadas à MESMA vizinha: a que sai da propriedade de cima fica em cima. Sem
     * isso as duas tinham baricentro idêntico, a ordem caía na declaração das coleções, e
     * o desenho ganhava um cruzamento que a posição das linhas já resolvia.
     */
    const fraction = (entry: Incidence) => {
        const height = heights[entry.neighbour]
        if (!height || entry.anchor === undefined) return 0.5
        return Math.min(Math.max(entry.anchor / height, 0), 0.999)
    }

    for (let pass = 0; pass < 2; pass++) {
        layers.forEach((layer, index) => {
            if (!index) return
            const previous = layers[index - 1]
            const positionOf = (alias: string) => previous.findIndex(node => node.alias === alias)
            const barycenter = (node: LayoutNode) => {
                const found = (arriving[node.alias] || [])
                    .map(entry => ({entry, position: positionOf(entry.neighbour)}))
                    .filter(found => found.position >= 0)
                if (!found.length) return Number.MAX_SAFE_INTEGER
                const sum = found.reduce((total, item) => total + item.position + fraction(item.entry), 0)
                return sum / found.length
            }
            const scored = layer.map((node, order) => ({node, order, score: barycenter(node)}))
            //order como desempate: sem ele a ordem dos nós sem vizinho ficaria à mercê do sort
            scored.sort((a, b) => a.score - b.score || a.order - b.order)
            layers[index] = scored.map(entry => entry.node)
        })
    }
}

/**
 * Empilha cada camada na transversal e centraliza as mais curtas contra a mais longa.
 *
 * A camada avança pela MAIOR extensão principal que contém — na horizontal isso é
 * sempre a largura da caixa, que é fixa; na vertical é a altura da caixa mais alta
 * daquela camada. O nó de passagem tem extensão principal zero e não conta aqui.
 */
function placeBoxes(orientation: Orientation, layers: LayoutNode[][]) {
    const gap = MAIN_GAP[orientation]
    const spans = layers.map(layer =>
        layer.reduce((sum, node) => sum + crossExtent(orientation, node), 0) +
        Math.max(0, layer.length - 1) * CROSS_GAP)
    const longest = Math.max(0, ...spans)
    const placed: PlacedNode[] = []

    let main = PADDING
    layers.forEach((layer, index) => {
        const depth = Math.max(0, ...layer.map(node => mainExtent(orientation, node)))
        let cross = PADDING + (longest - spans[index]) / 2
        layer.forEach(node => {
            //A passagem ocupa a coluna INTEIRA na direção principal, e não um ponto: é o
            //que permite à linha atravessar a coluna reta, na faixa reservada, e só mudar
            //de altura nos vãos entre colunas. Como ponto, o cotovelo virava no meio da
            //coluna e o trecho horizontal voltava a cortar a caixa vizinha.
            const along = node.crossing ? depth : mainExtent(orientation, node)
            const size = project(orientation, along, crossExtent(orientation, node))
            const at = project(orientation, main, cross)
            placed.push({
                alias: node.alias, x: at.x, y: at.y,
                width: size.x, height: size.y, crossing: node.crossing
            })
            cross += crossExtent(orientation, node) + CROSS_GAP
        })
        main += depth + gap
    })

    //main saiu do laço com um vão sobrando depois da última camada; no lugar dele entra
    //a folga da borda
    const size = project(orientation, main - gap + PADDING, longest + PADDING * 2)
    return {placed, width: size.x, height: size.y}
}

/**
 * Âncoras na borda de uma caixa, distribuídas na transversal.
 *
 * Várias arestas na mesma caixa precisam sair de pontos diferentes, senão viram uma
 * linha só. Distribui no miolo da borda (10% a 90%) para nenhuma encostar no canto.
 */
function anchorsFor(orientation: Orientation, box: LaidOutBox, count: number): number[] {
    const {start, size} = crossRange(orientation, box)
    if (count <= 1) return [start + size / 2]
    const from = start + size * 0.1
    const usable = size * 0.8
    return Array.from({length: count}, (_, i) => from + (usable * i) / (count - 1))
}

/**
 * Âncoras pedidas pelo chamador, afastadas o suficiente para não se sobreporem.
 *
 * Duas ligações pedem o mesmo ponto quando caem na mesma linha da caixa — o caso comum
 * é o cabeçalho, para onde vão todas as que o outro lado não espelha. Empilhadas no
 * mesmo pixel, elas viram uma seta só.
 *
 * Empurra cada uma para baixo da anterior e, se o conjunto estourar a caixa, centraliza
 * o bloco inteiro; se nem assim couber, devolve null e a distribuição igual assume. É o
 * limite honesto: a partir de certa quantidade não há como manter o alinhamento com a
 * linha e ainda separar as setas.
 */
function requestedAnchors(
    orientation: Orientation, box: LaidOutBox, wanted: number[]
): number[] | null {
    const {start, size} = crossRange(orientation, box)
    if (wanted.some(value => value === undefined)) return null
    if (wanted.length * MIN_ANCHOR_GAP > size) return null

    const placed: number[] = []
    wanted.forEach((value, i) => {
        placed.push(i ? Math.max(value, placed[i - 1] + MIN_ANCHOR_GAP) : value)
    })

    //O empurrão acumulado pode passar da borda; descer o bloco todo preserva o espaçamento
    const overflow = placed[placed.length - 1] - (start + size - MIN_ANCHOR_GAP / 2)
    const shift = overflow > 0 ? overflow : 0
    const shifted = placed.map(value => value - shift)
    return shifted[0] < start + MIN_ANCHOR_GAP / 2 ? null : shifted
}

type Waypoint = { main: number; cross: number }

/**
 * Liga dois pontos com um cotovelo ortogonal: anda na principal, desloca na transversal,
 * e volta a andar na principal.
 *
 * `fraction` escolhe ONDE no vão fica o deslocamento. Com várias ligações atravessando o
 * mesmo vão, é o que dá uma faixa a cada uma — no meio para todas, os trechos
 * transversais ficavam em cima uns dos outros e não dava para dizer qual linha ia para
 * qual caixa.
 */
function connect(orientation: Orientation, a: Waypoint, b: Waypoint, fraction: number) {
    const at = (main: number, cross: number) => project(orientation, main, cross)
    if (a.cross === b.cross) return [at(a.main, a.cross), at(b.main, b.cross)]
    const direction = b.main >= a.main ? 1 : -1
    const stubOut = a.main + direction * STUB
    const stubIn = b.main - direction * STUB
    const middle = stubOut + (stubIn - stubOut) * fraction
    return [at(a.main, a.cross), at(middle, a.cross), at(middle, b.cross), at(b.main, b.cross)]
}

/** Tira pontos repetidos em sequência, que o encadeamento de cotovelos produz nas junções. */
function simplify(points: { x: number; y: number }[]) {
    return points.filter((point, i) =>
        !i || point.x !== points[i - 1].x || point.y !== points[i - 1].y)
}

/**
 * Alça do lado de fora da caixa, para o relacionamento de uma coleção com ela mesma.
 *
 * Sai da linha da propriedade e volta na do espelho, quando as duas são conhecidas — é a
 * ligação mais fácil de ler errado, porque as duas pontas moram na mesma caixa. Sem esse
 * dado, ou empilhado, cai em dois terços da altura.
 */
function selfEdge(
    orientation: Orientation, box: LaidOutBox, link: DiagramLink,
    fromCross?: number, toCross?: number, lane = 0
): LaidOutEdge {
    const {end} = mainRange(orientation, box)
    const {start, size} = crossRange(orientation, box)
    const apart = fromCross !== undefined && toCross !== undefined &&
        Math.abs(fromCross - toCross) >= MIN_ANCHOR_GAP
    /**
     * Sem as linhas a que se prender — é o caso do empilhado —, a alça se abre por conta
     * própria, e a de faixa mais externa se abre MAIS. Uma abertura fixa punha as duas
     * alças de uma mesma caixa exatamente no mesmo lugar.
     */
    const inset = Math.max(0.1, 0.32 - lane * 0.14)
    const near = apart ? fromCross! : start + size * inset
    const far = apart ? toCross! : start + size * (1 - inset)
    //Uma faixa por alça: a coleção com dois autorrelacionamentos teria as duas no mesmo
    //lugar, uma por cima da outra
    const out = end + SELF_OUT + lane * SELF_STEP
    const at = (main: number, cross: number) => project(orientation, main, cross)
    return {
        id: link.id,
        from: link.from,
        to: link.to,
        points: [at(end, near), at(out, near), at(out, far), at(end, far)],
        source: at(end, near),
        target: at(end, far),
        forward: true,
        selfLink: true
    }
}

function indexOf(placed: PlacedNode[]): Record<string, PlacedNode> {
    const index: Record<string, PlacedNode> = {}
    placed.forEach(node => { index[node.alias] = node })
    return index
}

function routeEdges(
    orientation: Orientation, placed: PlacedNode[], links: DiagramLink[], paths: Record<number, string[]>
): LaidOutEdge[] {
    const index = indexOf(placed)
    const valid = links.filter(link => index[link.from] && index[link.to])

    const forwardOf = (link: DiagramLink) =>
        mainRange(orientation, index[link.to]).start >= mainRange(orientation, index[link.from]).end

    /**
     * O ponto que a aresta pede naquela borda, já em coordenada do desenho. Só na
     * horizontal: empilhado, a borda de saída corre na outra direção e não há linha da
     * caixa a que alinhar.
     */
    const wantedOn = (alias: string, link: DiagramLink, outgoing: boolean) => {
        if (orientation !== 'horizontal') return undefined
        const offset = outgoing ? link.fromAnchor : link.toAnchor
        return offset === undefined ? undefined : index[alias].y + offset
    }

    /**
     * Arestas que dividem a mesma borda da mesma caixa.
     *
     * O lado entra na chave porque uma aresta que volta para trás sai pela borda oposta:
     * as duas não disputam as mesmas âncoras e não devem ser distribuídas juntas.
     */
    const groups: Record<string, DiagramLink[]> = {}
    const keyOf = (alias: string, role: 'out' | 'in', forward: boolean) => `${role}:${alias}:${forward}`
    valid.forEach(link => {
        if (link.from === link.to) return
        const forward = forwardOf(link)
        const out = keyOf(link.from, 'out', forward)
        const into = keyOf(link.to, 'in', forward)
        if (!groups[out]) groups[out] = []
        if (!groups[into]) groups[into] = []
        groups[out].push(link)
        groups[into].push(link)
    })

    /**
     * Ordena cada borda pela posição da OUTRA ponta, e não pela ordem das propriedades.
     *
     * É o que desfaz o cruzamento: com a ordem de declaração, uma coleção que aponta
     * para duas outras dava a âncora de cima para a primeira propriedade mesmo quando o
     * destino dela estava embaixo — as duas linhas se encontravam no meio do vão e não
     * dava para dizer qual ia para qual. Ordenando pela posição do destino, a linha que
     * vai para cima também sai por cima.
     *
     * O id desempata: dois destinos na mesma altura manteriam a ordem à mercê do sort.
     */
    Object.keys(groups).forEach(key => {
        const outgoing = key.indexOf('out:') === 0
        groups[key].sort((a, b) => {
            const wantedA = wantedOn(outgoing ? a.from : a.to, a, outgoing)
            const wantedB = wantedOn(outgoing ? b.from : b.to, b, outgoing)
            if (wantedA !== undefined && wantedB !== undefined && wantedA !== wantedB) {
                return wantedA - wantedB
            }
            return centreOf(orientation, index[outgoing ? a.to : a.from]) -
                centreOf(orientation, index[outgoing ? b.to : b.from]) || a.id - b.id
        })
    })

    const anchors: Record<string, number[]> = {}
    const anchorFor = (alias: string, key: string, link: DiagramLink, outgoing: boolean) => {
        const group = groups[key]
        if (!anchors[key]) {
            const wanted = group.map(other => wantedOn(alias, other, outgoing))
            anchors[key] = requestedAnchors(orientation, index[alias], wanted as number[]) ||
                anchorsFor(orientation, index[alias], group.length)
        }
        return anchors[key][group.indexOf(link)]
    }

    //Resolve as pontas antes de rotear: a faixa de uma ligação depende de quantas outras
    //atravessam o mesmo vão, e isso só se sabe com todas as pontas na mão
    const routes = valid.filter(link => link.from !== link.to).map(link => {
        const forward = forwardOf(link)
        return {
            link,
            forward,
            fromCross: anchorFor(link.from, keyOf(link.from, 'out', forward), link, true),
            toCross: anchorFor(link.to, keyOf(link.to, 'in', forward), link, false)
        }
    })

    /**
     * O vão que a ligação atravessa: da borda de saída da origem à de entrada do destino.
     * Duas ligações com o mesmo vão disputam o mesmo espaço e precisam de faixas
     * diferentes — é o caso de uma coleção que aponta para duas outras da mesma coluna.
     *
     * Só as de um trecho entram: a longa já tem a sua faixa reservada pelo nó de passagem.
     */
    const corridors: Record<string, typeof routes> = {}
    routes.forEach(route => {
        //Linha reta não tem trecho transversal, então não disputa faixa com ninguém
        if (route.fromCross === route.toCross) return
        if ((paths[route.link.id] || []).length > 2) return
        const from = mainRange(orientation, index[route.link.from])
        const to = mainRange(orientation, index[route.link.to])
        const key = route.forward ? `${from.end}>${to.start}` : `${from.start}<${to.end}`
        if (!corridors[key]) corridors[key] = []
        corridors[key].push(route)
    })

    const lanes: Record<number, number> = {}
    Object.keys(corridors).forEach(key => {
        const group = corridors[key]
        //Pela ponta de chegada: a ligação que vai mais para cima fica na faixa de fora
        group.sort((a, b) => a.toCross - b.toCross || a.link.id - b.link.id)
        group.forEach((route, position) => {
            lanes[route.link.id] = (position + 1) / (group.length + 1)
        })
    })

    const routeById: Record<number, typeof routes[0]> = {}
    routes.forEach(route => { routeById[route.link.id] = route })

    /** A polilinha inteira, encadeando um cotovelo por trecho do caminho. */
    const polyline = (route: typeof routes[0]) => {
        const {link} = route
        const path = paths[link.id] || [link.from, link.to]
        //O caminho vem na ordem das camadas; aqui ele precisa ir da ORIGEM ao destino
        const walk = path[0] === link.from ? path : [...path].reverse()

        const first = mainRange(orientation, index[walk[0]])
        const last = mainRange(orientation, index[walk[walk.length - 1]])
        const waypoints: Waypoint[] = []
        walk.forEach((alias, i) => {
            if (i === 0) {
                waypoints.push({main: route.forward ? first.end : first.start, cross: route.fromCross})
            } else if (i === walk.length - 1) {
                waypoints.push({main: route.forward ? last.start : last.end, cross: route.toCross})
            } else {
                //Dois pontos, um em cada borda da coluna: entre eles a linha anda reta na
                //faixa reservada, e as viradas ficam nos vãos
                const node = index[alias]
                const {start, end} = mainRange(orientation, node)
                const cross = centreOf(orientation, node)
                waypoints.push({main: route.forward ? start : end, cross})
                waypoints.push({main: route.forward ? end : start, cross})
            }
        })

        const fraction = lanes[link.id] === undefined ? 0.5 : lanes[link.id]
        const points: { x: number; y: number }[] = []
        for (let i = 0; i + 1 < waypoints.length; i++) {
            points.push(...connect(orientation, waypoints[i], waypoints[i + 1], fraction))
        }
        return simplify(points)
    }

    /**
     * Faixa de cada alça, por caixa.
     *
     * A de maior alcance fica por FORA, e é o que faz as alças se aninharem em vez de se
     * cruzarem: os tocos da externa saem além das pontas da interna, então não cortam o
     * trecho de volta dela. Com a mais larga por dentro acontece o contrário — os tocos
     * da mais estreita atravessam o trecho de volta da mais larga.
     *
     * Ordenar pela declaração não serviria: duas alças intercaladas (Pai, Mãe, Pai de,
     * Mãe de) se cruzariam de qualquer jeito.
     */
    const selfLanes: Record<number, number> = {}
    const loops: Record<string, DiagramLink[]> = {}
    valid.filter(link => link.from === link.to).forEach(link => {
        if (!loops[link.from]) loops[link.from] = []
        loops[link.from].push(link)
    })
    const spanOf = (link: DiagramLink) => {
        const from = wantedOn(link.from, link, true)
        const to = wantedOn(link.from, link, false)
        return from === undefined || to === undefined ? 0 : Math.abs(from - to)
    }
    Object.keys(loops).forEach(alias => {
        //Crescente: a faixa 0 é a mais colada na caixa, e fica com o menor alcance
        loops[alias].sort((a, b) => spanOf(a) - spanOf(b) || a.id - b.id)
        loops[alias].forEach((link, position) => { selfLanes[link.id] = position })
    })

    return valid.map(link => {
        if (link.from === link.to) {
            return selfEdge(orientation, index[link.from], link,
                wantedOn(link.from, link, true), wantedOn(link.from, link, false),
                selfLanes[link.id])
        }
        const route = routeById[link.id]
        const points = polyline(route)
        return {
            id: link.id,
            from: link.from,
            to: link.to,
            points,
            source: points[0],
            target: points[points.length - 1],
            forward: route.forward,
            selfLink: false
        }
    })
}

/**
 * Estende o desenho para caber as LINHAS, e não só as caixas.
 *
 * A alça de um autorrelacionamento sai para fora da caixa, e o tamanho vinha só do
 * arranjo das colunas: numa coleção sozinha, ou na última coluna, a alça caía fora do
 * SVG e era cortada — sobrava o toco da seta apontando para o nada. Vale para qualquer
 * linha que escape, não só para a alça.
 *
 * Se algo ficou antes da origem, o desenho inteiro anda: coordenada negativa não é
 * desenhável, e é mais simples deslocar uma vez aqui do que cada rota se defender.
 */
function fitBounds(boxes: LaidOutBox[], edges: LaidOutEdge[], width: number, height: number) {
    let minX = 0
    let minY = 0
    let maxX = width - PADDING
    let maxY = height - PADDING
    const consider = (x: number, y: number) => {
        minX = Math.min(minX, x)
        minY = Math.min(minY, y)
        maxX = Math.max(maxX, x)
        maxY = Math.max(maxY, y)
    }
    boxes.forEach(box => {
        consider(box.x, box.y)
        consider(box.x + box.width, box.y + box.height)
    })
    edges.forEach(edge => edge.points.forEach(point => consider(point.x, point.y)))

    //Só o que saiu para fora da origem desloca. O desenho já nasce com a folga da borda,
    //e deslocar por menos que isso só mexeria nas coordenadas sem motivo.
    const dx = minX < 0 ? PADDING - minX : 0
    const dy = minY < 0 ? PADDING - minY : 0
    if (dx || dy) {
        boxes.forEach(box => { box.x += dx; box.y += dy })
        edges.forEach(edge => {
            edge.points.forEach(point => { point.x += dx; point.y += dy })
            edge.source = {x: edge.source.x + dx, y: edge.source.y + dy}
            edge.target = {x: edge.target.x + dx, y: edge.target.y + dy}
        })
    }
    return {width: maxX + dx + PADDING, height: maxY + dy + PADDING}
}

export function layoutDiagram(
    nodes: DiagramNode[], links: DiagramLink[], orientation: Orientation = 'horizontal'
): Diagram {
    if (!nodes.length) return {boxes: [], edges: [], width: 0, height: 0, orientation}
    const heights: Record<string, number> = {}
    nodes.forEach(node => { heights[node.alias] = node.height })

    const layer = layerOf(nodes, links)
    const {all, steps, paths} = splitLongLinks(nodes, links, layer)
    const layers = layersOf(all, layer)
    sortLayers(layers, steps, heights)
    const {placed, width, height} = placeBoxes(orientation, layers)

    const edges = routeEdges(orientation, placed, links, paths)
    //Os nós de passagem não são caixas: existem só para reservar o caminho
    const boxes = placed.filter(node => !node.crossing)
        .map(({alias, x, y, width: w, height: h}) => ({alias, x, y, width: w, height: h}))
    const fitted = fitBounds(boxes, edges, width, height)
    return {boxes, edges, width: fitted.width, height: fitted.height, orientation}
}

/**
 * Largura mínima para o diagrama horizontal fazer sentido: duas camadas lado a lado.
 * Abaixo disso a leitura da esquerda para a direita não cabe, e empilhar as camadas
 * de cima para baixo aproveita a única dimensão que sobra num telefone.
 */
export const HORIZONTAL_MIN_WIDTH = BOX_WIDTH * 2 + MAIN_GAP.horizontal + PADDING * 2

export function orientationFor(viewportWidth: number): Orientation {
    return viewportWidth >= HORIZONTAL_MIN_WIDTH ? 'horizontal' : 'vertical'
}
